import { useState, useEffect } from "react";
import * as api from "../../apiService";
import { useToast } from "../../components/Toast";
import { todayDubai } from "../../utils/dateUtils";

const STATUS_CONFIG = {
  SUBMITTED: { label: "Submitted",  color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
  RECEIVED:  { label: "Received",   color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  TESTED:    { label: "Tested",     color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
};

const OUTCOME_CONFIG = {
  GRADE_MATCH: { label: "Grade Match",         color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  NEW_PRODUCT: { label: "New Product Needed",  color: "#c2410c", bg: "#fff7ed", border: "#fed7aa" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.SUBMITTED;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
    }}>{cfg.label}</span>
  );
}

function OutcomeBadge({ outcome }) {
  if (!outcome) return null;
  const cfg = OUTCOME_CONFIG[outcome];
  if (!cfg) return null;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
    }}>{cfg.label}</span>
  );
}

function SubmitModal({ onSave, onCancel }) {
  const [form,   setForm]   = useState({ sampleRef: "", customerName: "", submittedDate: todayDubai(), notes: "" });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function set(f, v) { setForm(p => ({ ...p, [f]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.sampleRef.trim())    { setError("Sample reference is required."); return; }
    if (!form.customerName.trim()) { setError("Customer name is required."); return; }
    setSaving(true); setError(null);
    try {
      await onSave({
        sampleRef:     form.sampleRef.trim(),
        customerName:  form.customerName.trim(),
        submittedDate: form.submittedDate,
        notes:         form.notes.trim() || null,
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal">
      <div className="modal-header">
        <h3 className="modal-title">Submit Customer Sample</h3>
        <button className="btn" onClick={onCancel}>✕</button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <span className="form-label">Date</span>
          <input type="date" className="form-input" value={form.submittedDate}
            onChange={e => set("submittedDate", e.target.value)} required style={{ width: 160 }} />
        </div>
        <div className="form-row">
          <span className="form-label">Customer *</span>
          <input className="form-input" placeholder="Customer name"
            value={form.customerName} onChange={e => set("customerName", e.target.value)}
            style={{ flex: 1 }} />
        </div>
        <div className="form-row">
          <span className="form-label">Sample Ref *</span>
          <input className="form-input" placeholder="e.g. S-2026-001 or product description"
            value={form.sampleRef} onChange={e => set("sampleRef", e.target.value)}
            style={{ flex: 1 }} />
        </div>
        <div className="form-row">
          <span className="form-label">Notes</span>
          <textarea className="form-input" rows={2} placeholder="Optional context for R&D…"
            value={form.notes} onChange={e => set("notes", e.target.value)}
            style={{ flex: 1, resize: "vertical" }} />
        </div>
        {error && <div className="alert-warn" style={{ marginTop: 8 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button type="button" className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Submitting…" : "Submit to R&D"}
          </button>
        </div>
      </form>
    </div>
  );
}

export function PageSalesCustomerSampleSubmissions({ userEmail = "" }) {
  const showToast = useToast();
  const [subs,    setSubs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [search,  setSearch]  = useState("");

  useEffect(() => {
    api.fetchSampleSubmissions()
      .then(setSubs)
      .catch(e => showToast(e.message, "error"))
      .finally(() => setLoading(false));
  }, []);

  async function handleCreate(body) {
    const created = await api.createSampleSubmission(body);
    setSubs(old => [created, ...old]);
    showToast("Sample submitted to R&D");
    setModal(false);
  }

  const filtered = search.trim()
    ? subs.filter(s =>
        (s.sample_ref ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (s.customer_name ?? "").toLowerCase().includes(search.toLowerCase()))
    : subs;

  return (
    <div style={{ padding: "20px 24px" }}>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div className="page-header-left">
          <div className="page-title">Customer Samples</div>
          <div className="page-subtitle">Track samples submitted to R&D for analysis</div>
        </div>
        <button className="btn btn-primary" onClick={() => setModal(true)}>+ Submit Sample</button>
      </div>

      <div className="action-bar">
        <input className="form-input" placeholder="Search by sample ref or customer…"
          value={search} onChange={e => setSearch(e.target.value)} style={{ width: 280 }} />
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af", fontSize: 13 }}>Loading…</div>
      )}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af", fontSize: 13 }}>
          No submissions yet. Click "Submit Sample" to log the first one.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 100 }}>Date</th>
                <th>Customer</th>
                <th>Sample Reference</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 160 }}>Outcome</th>
                <th>R&D Notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id}>
                  <td style={{ fontSize: 12, color: "#6b7280" }}>{s.submitted_date}</td>
                  <td style={{ fontWeight: 600 }}>{s.customer_name}</td>
                  <td>{s.sample_ref}</td>
                  <td><StatusBadge status={s.status} /></td>
                  <td><OutcomeBadge outcome={s.outcome} /></td>
                  <td style={{ fontSize: 12, color: "#6b7280" }}>{s.outcome_notes ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setModal(false); }}>
          <SubmitModal onSave={handleCreate} onCancel={() => setModal(false)} />
        </div>
      )}
    </div>
  );
}
